import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { History, Loader2, ChevronDown, ChevronUp } from "lucide-react";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (qui si usava date-fns).
import { formatDateTime } from "../lib/format";

const SOURCE_LABEL = {
  crm: "Gestionale",
  sara: "Sara",
  import: "Import",
  sistema: "Sistema",
  "correzione-manuale": "Correzione manuale",
};

function fmtValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Sì" : "No";
  if (Array.isArray(v)) return `${v.length} voci`;
  if (typeof v === "object") return "…";
  return String(v);
}

function fmtWhen(ts) {
  if (!ts) return "";
  try {
    return formatDateTime(ts, String(ts));
  } catch {
    return String(ts);
  }
}

/**
 * Storico modifiche (audit_log) di una polizza o di un cliente.
 * Caricamento su richiesta: non appesantisce l'apertura della scheda.
 */
export default function AuditHistory({ entityType, entityId, title = "Storico modifiche" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/insights/history/${entityType}/${entityId}`);
      setItems(res.data?.history || []);
      setLoaded(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore nel caricamento dello storico");
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded && !loading) load();
  };

  return (
    <Card data-testid="audit-history-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-sm flex items-center gap-2">
            <History className="h-4 w-4" />
            {title}
          </CardTitle>
          <CardDescription className="text-xs">
            Chi ha cambiato cosa e quando (ultime 50 modifiche)
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={toggle} data-testid="audit-history-toggle">
          {open ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
          {open ? "Chiudi" : "Mostra"}
        </Button>
      </CardHeader>
      {open && (
        <CardContent>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Carico lo storico…
            </div>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!loading && !error && items.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="audit-history-empty">
              Nessuna modifica registrata.
            </p>
          )}
          {!loading && !error && items.length > 0 && (
            <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
              {items.map((h, i) => (
                <div key={i} className="border-l-2 border-muted pl-3 py-1" data-testid={`audit-entry-${i}`}>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>{fmtWhen(h.timestamp)}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {SOURCE_LABEL[h.source] || h.source || "—"}
                    </Badge>
                    {h.action === "delete" && (
                      <Badge variant="outline" className="text-[10px] border-red-300 text-red-600">
                        eliminazione
                      </Badge>
                    )}
                  </div>
                  {h.note && <p className="text-xs italic text-muted-foreground mt-0.5">{h.note}</p>}
                  {Array.isArray(h.changes) && h.changes.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {h.changes.map((c, j) => (
                        <div key={j} className="text-xs">
                          <span className="text-muted-foreground">{c.label || c.field}:</span>{" "}
                          <span className="line-through text-muted-foreground">{fmtValue(c.before)}</span>
                          {" → "}
                          <span className="font-medium">{fmtValue(c.after)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
